import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Save } from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';

const WorkflowConfig = () => {
    const navigate = useNavigate();
    const [, setConfigs] = useState([]);
    const [defaultConfig, setDefaultConfig] = useState({
        notifyManager: true,
        notifyHR: true,
        notifyAdditionalEmails: '',
        requireManagerApproval: true,
        requireHRApproval: false
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const res = await api.get('/workflow/configs');
            setConfigs(res.data);

            // Find default config (leaveType === null)
            const defaultCfg = res.data.find(c => c.leaveType === null);
            if (defaultCfg) {
                setDefaultConfig({
                    notifyManager: defaultCfg.notifyManager,
                    notifyHR: defaultCfg.notifyHR,
                    notifyAdditionalEmails: defaultCfg.notifyAdditionalEmails || '',
                    requireManagerApproval: defaultCfg.requireManagerApproval,
                    requireHRApproval: defaultCfg.requireHRApproval
                });
            }
        } catch (error) {
            console.error('Error fetching configs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDefaultConfig = async () => {
        setSaving(true);
        try {
            await api.put('/workflow/config', {
                leaveType: null,
                ...defaultConfig
            });
            alert('Workflow configuration updated successfully');
            fetchConfigs();
        } catch (error) {
            console.error('Save error:', error);
            alert('Failed to update configuration');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container section">
            <PageHeader crumbs={crumbsFor('leaveWorkflow')} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    Workflow Configuration
                </h1>

                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                        onClick={() => navigate('/admin/departments')}
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <Settings size={16} /> Manage Departments
                    </button>
                    <div style={{ width: '0px' }} /> {/* Spacer */}
                </div>
            </div>

            {loading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Loading configuration...
                </div>
            ) : (
                <div className="glass-card" style={{ padding: '2rem' }}>
                    <div style={{ marginBottom: '2rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Settings size={20} /> Default Leave Workflow
                        </h2>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Configure default notification and approval settings for all leave types
                        </p>
                    </div>

                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                        {/* Notification Settings */}
                        <div>
                            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                Notification Settings
                            </h3>
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={defaultConfig.notifyManager}
                                        onChange={e => setDefaultConfig({ ...defaultConfig, notifyManager: e.target.checked })}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span>Notify Manager on leave application</span>
                                </label>

                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={defaultConfig.notifyHR}
                                        onChange={e => setDefaultConfig({ ...defaultConfig, notifyHR: e.target.checked })}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span>Notify HR on leave application</span>
                                </label>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                                        Additional Email Recipients (comma-separated)
                                    </label>
                                    <input
                                        type="text"
                                        value={defaultConfig.notifyAdditionalEmails}
                                        onChange={e => setDefaultConfig({ ...defaultConfig, notifyAdditionalEmails: e.target.value })}
                                        placeholder="email1@example.com, email2@example.com"
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.5rem' }}
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                                        These emails will receive notifications for all leave applications
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Approval Settings */}
                        <div>
                            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                Approval Settings
                            </h3>
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={defaultConfig.requireManagerApproval}
                                        onChange={e => setDefaultConfig({ ...defaultConfig, requireManagerApproval: e.target.checked })}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span>Require Manager Approval</span>
                                </label>

                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={defaultConfig.requireHRApproval}
                                        onChange={e => setDefaultConfig({ ...defaultConfig, requireHRApproval: e.target.checked })}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span>Require HR Approval (in addition to manager)</span>
                                </label>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                            <button
                                onClick={handleSaveDefaultConfig}
                                disabled={saving}
                                className="btn btn-primary"
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                            >
                                <Save size={18} />
                                {saving ? 'Saving...' : 'Save Configuration'}
                            </button>
                        </div>
                    </div>

                    {/* Info Box */}
                    <div style={{
                        marginTop: '2rem',
                        padding: '1rem',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        color: '#1e40af'
                    }}>
                        <strong>Note:</strong> These settings apply to all leave types by default. You can configure specific settings for individual leave types in future updates.
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkflowConfig;
